import {
  CategoryQuantity,
  ExistValidatorComparator,
  NumberValidatorComparator,
  TDynamicVariable,
} from "@shared/types";
import { CoreConfig, InternalCoreConfig } from "./types";
import {
  calculateStaticSubcategory,
  calculateSubcategory,
} from "./calculateType/subcategory";
import { roundNumber, usesParentValue } from "./utils";

import { calculateLine } from "./calculateType/lines";
import { calculateNumber } from "./calculateType/number";
import { calculatePoint } from "./calculateType/point";
import { calculateTakeoffVariable } from "./calculateType/takeoffVariable";
import { evaluate } from "mathjs";

//import { calculateCategory } from "./calculateType/category";
// Circular dependency preventes splitting category









export const calculateVariableValue = (
  variable: TDynamicVariable,
  config: InternalCoreConfig,
  debug = false
) => {
  try {
    switch (variable.variableType) {
      case "TAKEOFF_VARIABLE":
        return calculateTakeoffVariable(variable, config);
      case "PARENT_CATEGORY":
      case "CATEGORY":
        return calculateCategory(variable, config , debug);
      case "PARENT_SUBCATEGORY":
      case "SUBCATEGORY":
        return calculateSubcategory(variable, config, debug);
      case "NUMBER":
        return calculateNumber(variable, config);
      case "LINE":
        return calculateLine(variable, config , debug);
      case "POINT":
        return calculatePoint(variable, config);
      case "OPERATOR":
        return ` ${variable.operator || "*"} `;
      case "TEXT":
        return variable.text || "";
      case "PARENT_STATIC_SUBCATEGORY":
      case "STATIC_SUBCATEGORY":
        return calculateStaticSubcategory(variable, config, debug);
    }
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const evaluateOrNull = (expr: string , debug : boolean = false) => {
  try {

    const v =  JSON.parse(JSON.stringify(evaluate(expr)));
    if( debug ){
      console.log( `evaluateOrNull( ${expr} ) : ${JSON.stringify(v)}`);
    }
    if( v.unit && !v.number ){
      v.number = 0;
    }
    if( v.unit && v.value === null ){
      v.value = 0;
    }
    return v;

  } catch (e) {
    return null;
  }
};

const advancedToString = (
  variables: TDynamicVariable[],
  config: CoreConfig,
  debug = false
): string => {
  let string = config.advancedVariable || "";
  for (const dv of variables) {
    const value = calculateVariableValue(dv, config, debug);
    const r = new RegExp(`\\@\\[[^\\]]*\\]\\(${dv._id}\\)`, "g");
    // Variable Pattern @[T](de119951e13d)
    string = string.replace(r, String(value));
  }
  return string;
};

type TCalculateFormula = {
  number: number | null;
  string: string | null;
  unit: string | null;
};
const numberOrNull = ( input : any ) => {
  if( typeof input === "number" ){
    return input;
  }
  return input || null;
}
export const calculateFormula = (
  variables: TDynamicVariable[],
  config: InternalCoreConfig,
  debug = false
): TCalculateFormula => {
  const string = config.advancedVariable
    ? advancedToString(variables, config, debug)
    : variables
        .map((v) => {
          if( debug ){
            console.log( v );
          }
          return calculateVariableValue(
            v,
            { appendUnit: true, ...config },
            debug
          );
        })
        .join("");
  if( debug) {
    console.log( string);
  }
  const output = evaluateOrNull(string , debug );  
  if( debug) {
    console.log( `string '${string}' : ${output?.value}`);
  }
  let number = typeof output === "number" ? output : numberOrNull( output?.value );
  const unit = typeof output === number ? null : output?.unit || null;

  if (number && !Number.isNaN(number)) {
    number = roundNumber(number, config);
  }
  return {
    number,
    string,
    unit,
  };
};

const getTrueVal = (val: TCalculateFormula): string | number | null => {
  if (val.number !== null) {
    return val.number;
  }
  if (val.string !== null) {
    return val.string;
  }
  return null;
};

export const evaluateValidator = (
  input: TDynamicVariable[],
  comparitor: NumberValidatorComparator | ExistValidatorComparator,
  output: TDynamicVariable[],
  config: CoreConfig,
  debug = false
) => {
  const i = calculateFormula(input, config , debug );
  const o = calculateFormula(output, config , debug );
  
  const ival = getTrueVal(i);
  const oval = getTrueVal(o);
  if (debug) {
    console.log({ i, o , ival , oval , comparitor });
  }

  switch (comparitor) {
    case "=":
      return ival === oval;
    case ">":
      return (i?.number || 0 ) > (o?.number || 0 );
    case "<":
      return (i?.number || 0 ) < (o?.number || 0 );
    case ">=":
      return (i?.number || 0 ) >= (o?.number || 0 );
    case "<=":
      return (i?.number || 0 ) <= (o?.number || 0 );
    case "!=":
      return ival !== oval;
    case "exists":
      return i.number === 1;
    case "not_exists":
      return i.number === 0;
  }
};

export const calculateString = (
  variables: TDynamicVariable[],
  config: CoreConfig
): string => {
  return variables
    .map((v) =>
      calculateVariableValue(v, { roundTo: 2, appendUnit: true, ...config })
    )
    .join(" ");
};

export const calculateCategory = (
  variable: TDynamicVariable,
  config: InternalCoreConfig,
  debug = false
): number => {
  
  if (
    (variable.categoryId && usesParentValue(variable.categoryId, config) && variable.output !== "COMPUTED_QUANTITY") ||
    /^PARENT/.test(variable.variableType)
  ) {
    const { parent, ...rest } = config;
    return calculateCategory(
      { ...variable, variableType: "CATEGORY" },
      {
        ...rest,
        ...parent,
      }
    );
  }
  const categories = (config.ProductConfiguration?.OptionsList || []).reduce(
    (acc: string[], cv) => {
      const catId = cv?.SubCategory?.category || cv.SubCategory?.Category?._id;
      if (catId) {
        return [...acc, catId];
      }
      return acc;
    },
    []
  );
  switch (variable.output) {
    default:
    case "EXISTS":
      return categories.includes(variable?.categoryId || "NOT_CONFIGURED")
        ? 1
        : 0;

    case "COMPUTED_QUANTITY":
      if (config.depthCount && config.depthCount > 50) {
        return 0;
      }
      const validConfigs = (config.categoryInput || []).filter(
        (ci) => ci.categoryId === variable.categoryId
      );
      
      const primaryValue = validConfigs
        .map(
          (ci) =>
            calculateFormula(ci.quantity, {
              ...config,
              depthCount: (config.depthCount || 0) + 1,
            }).number || 0
        )
        .reduce((acc, cv) => acc + cv, 0);
      let extra = 0;
      for (const vc of validConfigs) {
        if (vc.categoryQuantities) {
          const validValidators = vc.categoryQuantities.filter((cq) => {
            return cq.validators
              .map((v) => {
                const e = evaluateValidator(
                  v.Input || [],
                  v.comparator,
                  v.Output || [],
                  config
                );
                return e;
              })
              .reduce((a, b) => a && b, true);
          });
          for (const validCQ of validValidators) {
            extra +=
              calculateFormula(validCQ.Quantity || [], {
                ...config,
                depthCount: (config.depthCount || 0) + 1,
              }).number || 0;
          }
        }
      }
      return roundNumber(primaryValue + extra, config);
  }
};
